/**
 * Formatting for a calculator's computed result.
 *
 * The Integration Health series needs more shapes than "a number": percentages,
 * whole-dollar costs, day and hour counts, ratios, percentage points, rates
 * expressed per month or per hundred instances, and record volumes large enough
 * to need thousands separators. Rather than a type per shape, there are three
 * numeric treatments and a free-text unit label the editor writes, which covers
 * every metric in the list.
 */

export type ResultValueType = 'currency' | 'number' | 'percent';

export interface ResultFormat {
  /** Fixed decimal places. Omitted, a result shows up to two and trims trailing zeros. */
  decimals?: number;
  /** ISO 4217 code. Only read when type is 'currency'. */
  currencyCode?: string;
  /** Prefix a positive result with '+'. For deltas, where the direction is the point. */
  showSign?: boolean;
  type?: ResultValueType;
  /** Trailing unit, e.g. 'days', 'hours', 'pp', 'per month'. Written by the editor. */
  unitLabel?: string;
}

const DEFAULT_MAXIMUM_DECIMALS = 2;

const LOCALE = 'en-US';

/**
 * Whole dollars by default. A currency result that trimmed trailing zeros would
 * render $24,750.5 rather than $24,750.50, which reads as a typo.
 */
const currencyDecimals = (decimals?: number): number => decimals ?? 0;

/**
 * Intl decides the sign from the ROUNDED value, which is what we want: -0.001
 * at two decimal places is displayed as zero, and a zero should not claim a
 * direction. 'negative' suppresses the sign on a negative that rounds away,
 * 'exceptZero' adds a leading plus to positives without ever signing a zero.
 */
const signDisplayFor = (showSign?: boolean): Intl.NumberFormatOptions['signDisplay'] =>
  showSign === true ? 'exceptZero' : 'negative';

const toNumberFormat = (type: ResultValueType, format: ResultFormat): Intl.NumberFormatOptions => {
  const signDisplay = signDisplayFor(format.showSign);

  if (type === 'currency') {
    const decimals = currencyDecimals(format.decimals);

    return {
      currency: format.currencyCode ?? 'USD',
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
      signDisplay,
      style: 'currency',
    };
  }

  if (format.decimals === undefined) {
    return { maximumFractionDigits: DEFAULT_MAXIMUM_DECIMALS, minimumFractionDigits: 0, signDisplay };
  }

  return { maximumFractionDigits: format.decimals, minimumFractionDigits: format.decimals, signDisplay };
};

/**
 * Format a computed result for display.
 *
 * @param value - The number the formula produced
 * @param format - How the editor configured this result
 * @returns The display string, or an empty string if the value is not a number
 */
export const formatResultValue = (value: number, format: ResultFormat = {}): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const type = format.type ?? 'number';
  const formatted = new Intl.NumberFormat(LOCALE, toNumberFormat(type, format)).format(value);
  const withPercent = type === 'percent' ? `${formatted}%` : formatted;
  const unitLabel = format.unitLabel?.trim();

  return unitLabel === undefined || unitLabel === '' ? withPercent : `${withPercent} ${unitLabel}`;
};
